import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { ParticleField } from '../shared/ParticleField';
import { ChevronDownIcon } from 'lucide-react';
import { HERO_BACKGROUND_IMAGE } from '../../data/signs';
export function Hero() {
  const [imgLoaded, setImgLoaded] = useState(false);
  const scrollToGrid = () => {
    document.getElementById('sign-grid')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative w-full h-screen flex items-center justify-center overflow-hidden bg-cosmic-black" data-id="element-55">
      {/* Immediate CSS-only cosmic background — visible instantly even on slow networks */}
      <div className="absolute inset-0 z-0" style={{
      background: 'radial-gradient(ellipse at center, #1a0f2e 0%, #0a0a18 40%, #050508 80%)'
    }} data-id="element-56" />

      {/* Background image — lazy, fades in once loaded so the hero never blocks paint */}
      <div className="absolute inset-0 z-[1]" data-id="element-57">
        <img src={HERO_BACKGROUND_IMAGE} alt="" aria-hidden="true" loading="lazy" decoding="async" onLoad={() => setImgLoaded(true)} className="w-full h-full object-cover transition-opacity duration-1000" style={{
        opacity: imgLoaded ? 0.4 : 0
      }} data-id="element-58" />
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, rgba(5,5,8,0.7) 0%, rgba(5,5,8,0.5) 50%, rgba(5,5,8,0.85) 100%)'
      }} data-id="element-59" />
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(circle at center, transparent 0%, rgba(5,5,8,0.6) 80%, rgba(5,5,8,1) 100%)'
      }} data-id="element-60" />
      </div>

      <ParticleField type="cosmic" density={30} data-id="element-61" />

      {/* Constellation overlay */}
      <div className="absolute inset-0 opacity-10 z-[2]" aria-hidden="true" data-id="element-62">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" data-id="element-63">
          <defs data-id="element-64">
            <pattern id="constellation" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse" data-id="element-65">
              <circle cx="10" cy="10" r="1" fill="#C9A84C" data-id="element-66" />
              <circle cx="150" cy="50" r="1" fill="#C9A84C" data-id="element-67" />
              <circle cx="80" cy="120" r="1" fill="#C9A84C" data-id="element-68" />
              <circle cx="180" cy="180" r="1" fill="#C9A84C" data-id="element-69" />
              <line x1="10" y1="10" x2="150" y2="50" stroke="#C9A84C" strokeWidth="0.5" opacity="0.3" data-id="element-70" />
              <line x1="150" y1="50" x2="80" y2="120" stroke="#C9A84C" strokeWidth="0.5" opacity="0.3" data-id="element-71" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#constellation)" data-id="element-72" />
        </svg>
      </div>

      <div className="relative z-10 text-center px-6 max-w-4xl" data-id="element-73">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} data-id="element-74">
          <h1 className="text-6xl sm:text-7xl md:text-9xl font-serif font-bold mb-6 tracking-widest" style={{
          color: '#F5F5F0',
          textShadow: '0 0 40px rgba(201, 168, 76, 0.7), 0 4px 30px rgba(0,0,0,0.9)'
        }} data-id="element-75">
            LOS 12
          </h1>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="text-gold-light text-base sm:text-lg md:text-xl mb-8 sm:mb-12 tracking-wide" style={{
        textShadow: '0 2px 20px rgba(0,0,0,0.9)'
      }} data-id="element-76">
          Doce entidades. Doce energías. Doce reinos.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="space-y-4 sm:space-y-6" data-id="element-77">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-serif font-semibold text-white mb-4" style={{
          textShadow: '0 2px 30px rgba(0,0,0,0.95)'
        }} data-id="element-78">
            Selecciona tu signo.
          </h2>
          <p className="text-gray-200 text-sm sm:text-base md:text-lg max-w-2xl mx-auto leading-relaxed" style={{
          textShadow: '0 2px 20px rgba(0,0,0,0.9)'
        }} data-id="element-79">
            Cada entidad representa una naturaleza distinta. Entra a tu reino y
            descubre el arquetipo que vive detrás de tu signo.
          </p>
        </motion.div>

        <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.7
      }} onClick={scrollToGrid} className="mt-10 sm:mt-12 px-6 sm:px-8 py-3 sm:py-4 bg-cosmic-black/40 backdrop-blur-sm border-2 border-gold-primary text-gold-light text-sm sm:text-base font-medium rounded-lg hover:bg-gold-primary hover:text-cosmic-black transition-all duration-300" whileHover={{
        scale: 1.05,
        boxShadow: '0 0 30px rgba(201, 168, 76, 0.5)'
      }} whileTap={{
        scale: 0.95
      }} data-id="element-80">
          Explora los 12 arquetipos
        </motion.button>

        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} className="mt-12 sm:mt-16" data-id="element-81">
          <ChevronDownIcon size={28} className="mx-auto text-gold-primary opacity-60" data-id="element-82" />
        </motion.div>
      </div>
    </section>;
}